import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Col } from 'antd';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { EmptyState } from '../empty_state/EmptyState';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import {
	getBuyerCompanySettingValue,
	isThirdPartySupplier,
	PERMISSION_NAMES,
} from '../../utils/AuthUtils';
import { ASSETS_CRUD_ACTION_CREATORS } from '../../actions/assets_actions';
import {
	assetsRestCrudThunks,
	assetsSearchAndAPIAdaptor,
	downloadAssetCSVForBuyer,
	downloadAssetCSVForSupplier,
} from '../../thunks/assets_thunks';
import { refreshRefrigerantBandCounts } from '../../thunks/refrigerant_events_thunks';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { useFilters } from '../../hooks/useFilters';
import { getRefrigerantAssetFilterConfig } from '../../utils/AssetFilterUtils';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { withUserType } from '../withUserType';
import {
	REFRIGERANT_ASSETS_TC,
	REFRIGERANT_ASSETS_DEFAULT_SORTER,
	REFRIGERANT_ASSET_SORTERS,
	REFRIGERANT_BUCKETS_FILTER_KEY,
	REFRIGERANT_BUCKET_PIPELINE_FILTERS,
	toRefrigerantApiFilters,
} from '../../utils/RefrigerantUtils';
import RefrigerantAssetRowDisplay from '../refrigerant/RefrigerantAssetRowDisplay';
import RefrigerantSubnav, { REFRIGERANT_SUBNAV_KEYS } from '../refrigerant/RefrigerantSubnav';
import CorporateLeakRateDisplay from '../refrigerant/CorporateLeakRateDisplay';
import LogRefrigerantEventDrawer from '../refrigerant/LogRefrigerantEventDrawer';

/**
 * The filter-OPTION dropdowns (locations, asset types) reuse the shared `assetsAdvancedFilters`
 * collection rather than a refrigerant-specific one. Each async filter looks its target collection
 * up in its OWN entity slice, so a bespoke name would have to be registered in every reducer the
 * filters read from (locations, regions, asset_types, …) or the dropdown throws
 * "invalid target collection name" on render. This name is already registered across all of them.
 * It only ever holds fetched dropdown options — the page's own list, filters and paging live in
 * REFRIGERANT_ASSETS_TC — so sharing it with the assets index is safe.
 */
const REFRIGERANT_ASSETS_ADVANCED_FILTERS = 'assetsAdvancedFilters';

/**
 * The stable empty-filters object. Shared rather than re-created so that `activeFilters` keeps a
 * constant identity while no filter is set — see the memo below for why that matters.
 */
const NO_ACTIVE_FILTERS = {};

/**
 * Refrigerant Tracking ▸ Assets — the compliance view of the tracked estate.
 *
 * Every regulated number on this screen comes out of the OpenSearch projection, so the list MUST go
 * through the search endpoint rather than the SQL listing: the derived scalars (leak rate, band, due
 * dates) are not on the SQL read path at all, and a page served from it would render blanks in every
 * column that matters.
 */
const RefrigerantAssetsIndexPage: FC<any> = ({
	assets,
	companyConfig,
	currentUser,
	userType,
	history,
	location,
	updateFilters,
	updateSorters,
	clearAndUpdateFilters,
	refreshCounts,
	downloadCSV,
}) => {
	const [logEventAsset, setLogEventAsset] = useState(null);

	/**
	 * The instant "due" is measured against, and the table's remount key.
	 *
	 * One value doing both jobs on purpose. It is the bound the inspection- and verification-due
	 * filters are evaluated at, so the list and the counts have to be computed against the SAME
	 * instant — otherwise a chip can read 34 while the list it opens shows 33, because the two
	 * requests straddled a second boundary. Re-stamping it after a write is also exactly when the
	 * table needs to re-read, since a logged event moves the leak rate and can move the appliance
	 * into a different bucket.
	 */
	const [nowMillis, setNowMillis] = useState(() => Date.now());

	const thresholdOverrides = useMemo(
		() =>
			getBuyerCompanySettingValue(userType)(
				'refrigerantConfig.thresholdOverrides',
				companyConfig,
				currentUser,
				[]
			),
		[companyConfig, currentUser, userType]
	);

	/**
	 * Translates the screen's own bucket selection into the filter keys the asset search understands.
	 *
	 * This is the only place that translation may happen. `refrigerantBuckets` is a frontend-only key
	 * and the server SILENTLY IGNORES filter keys it does not recognise — so a request that skipped
	 * this would return the whole estate while the chip showed as selected, with nothing to notice it.
	 */
	const fetchRefrigerantAssets = useCallback(
		(params, targetCollectionName, pagination, sorting, filters) => {
			const apiFilters = toRefrigerantApiFilters(
				{ ...(params || {}), ...(filters || {}) },
				nowMillis
			);
			return assetsRestCrudThunks(userType).searchWithPostMethod(
				apiFilters,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				false,
				assetsSearchAndAPIAdaptor
			);
		},
		[nowMillis, userType]
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				const path = `/${userType}/assets/detail/${nullSafeGet(
					'id',
					record
				)}/refrigerantTracking?targetCollectionName=${REFRIGERANT_ASSETS_TC}`;
				history.push(
					getLinkWIthBackLinkParams(location, 'Back to Refrigerant Tracking', path, {
						preserve: true,
					})
				);
			},
		}),
		[history, location, userType]
	);

	const onRaiseLeakRepair = useCallback(
		(asset) =>
			history.push(
				getLinkWIthBackLinkParams(
					location,
					'Back to Refrigerant Tracking',
					`/${userType}/assets/${nullSafeGet('id', asset)}/newWorkOrder`
				)
			),
		[history, location, userType]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Asset',
				dataIndex: 'name',
				render: (_, record) => (
					<RefrigerantAssetRowDisplay
						asset={record}
						thresholdOverrides={thresholdOverrides}
						onLogEvent={setLogEventAsset}
						onRaiseLeakRepair={onRaiseLeakRepair}
						// A sub-asset is not a work-order target anywhere else in the product — the asset
						// detail page and AssetWithQRCodeDisplay both suppress Request Service whenever
						// `parentId` is set — so this list must not be the one screen that offers it.
						canRaiseWorkOrder={!nullSafeGet('parentId', record)}
					/>
				),
			},
		],
		[onRaiseLeakRepair, thresholdOverrides]
	);

	const allFilters = useFilters({
		filterTargetCollectionName: REFRIGERANT_ASSETS_ADVANCED_FILTERS,
		overviewCollectionName: REFRIGERANT_ASSETS_TC,
		updateFilters: useCallback(
			(filters) => updateFilters(filters, REFRIGERANT_ASSETS_TC),
			[updateFilters]
		),
	});

	const filterConfig = useMemo(
		() => getRefrigerantAssetFilterConfig({ filters: allFilters, currentUser }),
		[allFilters, currentUser]
	);

	// `NO_ACTIVE_FILTERS`, not a `{}` literal, and that is the whole point of the constant. This memo
	// is keyed on the entire `assets` slice, so it recomputes on every dispatch that touches it —
	// fetch start, fetch success, count updates. With a literal fallback the unfiltered case handed
	// back a BRAND NEW object each time, and `CorporateLeakRateDisplay`'s effect is keyed on this
	// value, so it re-POSTed the corporate leak-rate aggregation several times per table load. A
	// shared constant makes the unfiltered case referentially stable; once filters are set the stored
	// redux object is already stable on its own.
	const activeFilters = useMemo(
		() => nullSafeGetOrElse(`${REFRIGERANT_ASSETS_TC}.filters`, assets, NO_ACTIVE_FILTERS),
		[assets]
	);

	// Export what the user is LOOKING AT. `refrigerantBuckets` is a frontend-only key, so the band and
	// due-status chips have to go through `toRefrigerantApiFilters` exactly as the table's own fetch
	// does — an untranslated bucket would be silently ignored and the file would quietly cover the whole
	// estate while the chip read as selected.
	const onDownloadCSV = useCallback(
		() => downloadCSV(toRefrigerantApiFilters(activeFilters, nowMillis)),
		[activeFilters, downloadCSV, nowMillis]
	);

	const onLogEventClick = useCallback(() => setLogEventAsset({}), []);

	// See the comment on the Log Event button below.
	const canLogEvent = !isThirdPartySupplier(currentUser);

	const onLogEventSuccess = useCallback(() => {
		setLogEventAsset(null);
		// Re-stamping `now` remounts the table (it is the `key`) and re-evaluates the due bounds, so
		// the list, the chips and the "due" cutoff all come from the same post-write instant.
		setNowMillis(Date.now());
		refreshCounts(activeFilters);
	}, [activeFilters, refreshCounts]);

	return (
		<>
			<PaginatedReduxTableWithHeader
				key={nowMillis}
				targetCollectionName={REFRIGERANT_ASSETS_TC}
				entityCollectionName="assets"
				stateSlice={assets}
				updateFilters={updateFilters}
				updateSorters={updateSorters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				refreshCounts={refreshCounts}
				pipeLineFilters={REFRIGERANT_BUCKET_PIPELINE_FILTERS}
				overridePipelineFilterKey={REFRIGERANT_BUCKETS_FILTER_KEY}
				filterConfig={filterConfig}
				sorters={REFRIGERANT_ASSET_SORTERS}
				initialSorters={REFRIGERANT_ASSETS_DEFAULT_SORTER}
				initialPagination={{ current: 1, pageSize: 25 }}
				tableColumns={columns}
				// The leak rate, the band and the trailing-12-month pounds live ONLY on the search doc.
				// A SQL record for the same asset (the Log Event picker reads one, so does the detail
				// page) otherwise wins in the store and blanks every regulated column on the row while
				// the SQL-backed ones next to it — charge, refrigerant, category — keep rendering.
				preferSearchRecords
				showHeader={false}
				onTableRow={onRow}
				fetchData={fetchRefrigerantAssets}
				leftActions={
					<RefrigerantSubnav activeKey={REFRIGERANT_SUBNAV_KEYS.ASSETS} userType={userType} />
				}
				// The subnav switches between two different ledgers; the band chips filter within
				// whichever one is showing. The tabs therefore have to sit above the chips.
				pipelineFiltersBelowHeader
				rightActions={
					<Col style={{ textAlign: 'right' }}>
						<div className="flex flex-row items-center justify-end gap-3">
							<CorporateLeakRateDisplay filters={activeFilters} />
							{/* Gated separately, and each checker takes a single child. This is the generic
							    asset CSV — the same `csv_report_generator` endpoint the assets index exposes
							    under SHOW_ASSET_PRICE, and it carries pricing — so it follows that permission
							    rather than MODIFY_ASSETS. Log Event is the only write here. */}
							<AccessPermissionChecker name={PERMISSION_NAMES.SHOW_ASSET_PRICE}>
								<OWButtonWithSupplierManagedLock size="large" onClick={onDownloadCSV}>
									Export CSV
								</OWButtonWithSupplierManagedLock>
							</AccessPermissionChecker>
							{/* A third-party supplier is excluded on top of MODIFY_ASSETS. Neither checker covers
							    this persona — `AccessPermissionChecker` checks the permission, and
							    `OWButtonWithSupplierManagedLock` locks supplier-MANAGED buyers — and the server
							    refuses their event POST outright without a private buyer company (see
							    refrigerant_events_thunks). Without the gate the drawer opens, the tech fills in
							    a regulated reading, and the save cannot succeed. Same rule as `canLogEvent` on
							    the asset detail tab. */}
							{canLogEvent ? (
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
									<OWButtonWithSupplierManagedLock
										size="large"
										type="primary"
										onClick={onLogEventClick}
									>
										Log Event
									</OWButtonWithSupplierManagedLock>
								</AccessPermissionChecker>
							) : null}
						</div>
					</Col>
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="No refrigerant-tracked appliances"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
							/>
						}
						headline={'No appliances in the refrigerant programme yet.'}
						body={
							<div style={{ textAlign: 'center', maxWidth: 440 }}>
								Turn on "Uses Refrigerant" on an asset to enrol it — its charge, refrigerant and
								appliance category are what the leak rate is computed from.
							</div>
						}
					/>
				}
			/>
			{logEventAsset ? (
				<LogRefrigerantEventDrawer
					visible
					// An empty object means "opened from the header" — no appliance chosen yet, so the
					// drawer shows its own asset picker.
					asset={nullSafeGet('id', logEventAsset) ? logEventAsset : undefined}
					onCancel={() => setLogEventAsset(null)}
					onSuccess={onLogEventSuccess}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = (state) => ({
	assets: state.assets,
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(ASSETS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(ASSETS_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(ASSETS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
	refreshCounts: (filters) => dispatch(refreshRefrigerantBandCounts(ownProps.userType)(filters)),
	// `filters` already carries `isRefrigerantTracked` — `toRefrigerantApiFilters` pins it — but it is
	// spread first so the tracked-only scope of this screen cannot be dropped by a caller.
	downloadCSV: (filters) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? downloadAssetCSVForSupplier({}, { isRefrigerantTracked: true, ...filters })
				: downloadAssetCSVForBuyer({}, { isRefrigerantTracked: true, ...filters })
		),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(RefrigerantAssetsIndexPage as any)
	) as any
);
